import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Alert, Stack } from "@mui/material";

import PageContainer from "../components/PageContainer";
import PageSection from "../components/PageSection";
import { useAccountHandlers } from "../hooks/useAccountHandlers";
import { NoticeboardButton, ActionModal } from "../components/shared";
import { useRegisterPageActions } from "../context/PageActionsContext";
import ProfileImageSelectorModal from "../components/ProfileImageSelectorModal";
import ImageCropperModal from "../components/Advertisements/ImageCropperModal";
import { useDashboardData } from "../hooks/useDashboardData";
import { useNotices } from "../hooks/useNotices";
import { useCircles } from "../hooks/useCircles";
import VideoPlayerModal from "../components/VideoPlayerModal";
import OrganisationSetupBanner from "../components/shared/OrganisationSetupBanner";
import GettingStartedChecklist from "../components/shared/GettingStartedChecklist";
import {
  OrganizationDetailsPanel,
  ProfileSummaryPanel,
  EmailPreferencesPanel,
  PublicPageSettingsPanel,
} from "../components/Profile";
import { Typography } from "@mui/material";
import { useSnackbar } from "../context/SnackbarContext";
import { ORGANIZATION_BANNER_ASPECT_RATIO } from "../utils/imageUpload";

const ProfilePageActions = {};

const Profile: React.FC = () => {
  const { user, updateUser, logout, token } = useAuth();
  const { isUserManagementEnabled, isCircleManagementEnabled } =
    useFeatureFlags();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);

  // Data for Getting Started checklist
  const { data: dashboardData } = useDashboardData();
  const { data: notices } = useNotices();
  const { data: circles = [] } = useCircles();

  const hasUsers = (dashboardData?.totalUsers || 0) > 0;
  const hasNotices = Boolean(notices && notices.length > 0);
  const hasSubcircles = circles.length >= 2;

  const checklistItems = useMemo(
    () => [
      ...(isUserManagementEnabled
        ? [
            {
              label: "Add your first user",
              done: hasUsers,
              onClick: () => navigate("/user-management"),
              videoPath: "/videos/adduser.webm",
              videoTitle: "Add User Tutorial",
            },
          ]
        : []),
      {
        label: "Create your first notice",
        done: hasNotices,
        onClick: () => navigate("/create-notice"),
        videoPath: "/videos/createnotice.webm",
        videoTitle: "Create Notice Tutorial",
      },
      ...(isCircleManagementEnabled
        ? [
            {
              label: "Add your subcircle",
              done: hasSubcircles,
              onClick: () => navigate("/circles"),
            },
          ]
        : []),
    ],
    [
      hasUsers,
      hasNotices,
      hasSubcircles,
      isUserManagementEnabled,
      isCircleManagementEnabled,
      navigate,
    ]
  );

  // Logo version state for cache-busting
  const [logoVersion, setLogoVersion] = useState(() => {
    const stored = localStorage.getItem("logoVersion");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [logoTimestamp, setLogoTimestamp] = useState(() => {
    const stored = localStorage.getItem("logoTimestamp");
    return stored ? parseInt(stored, 10) : 0;
  });

  // Check if user should be able to edit (only Owners can edit)
  const canEdit =
    user?.userType === "admin" ||
    (user?.userType === "team" && user?.accessLevel === "OWNER");
  const canManageEmailPreferences = user?.userType !== "team";

  const {
    handleLogoError,
    handleLogoFileUpload,
    handleBannerFileUpload,
    handleUpdateSchoolName,
    handleUpdateSchoolDescription,
    handleUpdatePhoneNumber,
    handleUpdateWebsite,
    logoError,
    uploadStatus,
    handleLogout,
    handleForgotPassword,
    isUpdatingName,
    isUpdatingDescription,
    isUpdatingPhoneNumber,
    isUpdatingWebsite,
    handleUpdatePublicPage,
    isUpdatingPublicPage,
    handleUpdatePublicPageSettings,
    isUpdatingPublicPageSettings,
    handleUpdateHidePhoneInApp,
    isUpdatingHidePhoneInApp,
  } = useAccountHandlers({ user, updateUser, logout, navigate, token });

  // State for image selector modal
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // State for banner upload
  const [isBannerCropperOpen, setIsBannerCropperOpen] = useState(false);
  const [bannerImageToCrop, setBannerImageToCrop] = useState<string | null>(
    null
  );
  const [bannerVersion, setBannerVersion] = useState(() => {
    const stored = localStorage.getItem("bannerVersion");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [bannerTimestamp, setBannerTimestamp] = useState(() => {
    const stored = localStorage.getItem("bannerTimestamp");
    return stored ? parseInt(stored, 10) : 0;
  });

  // Register page actions for unified navigation (stable empty object)
  useRegisterPageActions(ProfilePageActions);

  // Opened at a section, e.g. #plan from Ask Noticeboard
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [hash]);

  const handleImageSelect = useCallback(
    async (file: File) => {
      try {
        const uploadSucceeded = await handleLogoFileUpload(file);
        if (!uploadSucceeded) {
          throw new Error("Logo upload failed");
        }

        const newVersion = logoVersion + 1;
        const newTimestamp = Date.now();
        setLogoVersion(newVersion);
        setLogoTimestamp(newTimestamp);
        localStorage.setItem("logoVersion", newVersion.toString());
        localStorage.setItem("logoTimestamp", newTimestamp.toString());
        setIsImageSelectorOpen(false);
      } catch (error) {
        console.error("Image upload error:", error);
      }
    },
    [handleLogoFileUpload, logoVersion]
  );

  const handleBannerFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        showMessage({
          message: "Please select an image file.",
          severity: "warning",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setBannerImageToCrop(dataUrl);
        setIsBannerCropperOpen(true);
      };
      reader.readAsDataURL(file);
    },
    [showMessage]
  );

  const handleBannerCropComplete = useCallback(
    async (croppedImageUrl: string) => {
      const saved = await handleBannerFileUpload(croppedImageUrl);
      if (!saved) throw new Error("Banner upload failed. Please try again.");
      const newVersion = bannerVersion + 1;
      const newTimestamp = Date.now();
      setBannerVersion(newVersion);
      setBannerTimestamp(newTimestamp);
      localStorage.setItem("bannerVersion", newVersion.toString());
      localStorage.setItem("bannerTimestamp", newTimestamp.toString());
      setIsBannerCropperOpen(false);
      setBannerImageToCrop(null);
    },
    [handleBannerFileUpload, bannerVersion]
  );

  // Save handlers that match EditableField's (value: string) => Promise<void> signature
  const onSaveName = useCallback(
    async (value: string) => {
      await handleUpdateSchoolName(value);
    },
    [handleUpdateSchoolName]
  );

  const onSaveDescription = useCallback(
    async (value: string) => {
      await handleUpdateSchoolDescription(value);
    },
    [handleUpdateSchoolDescription]
  );

  const onSavePhoneNumber = useCallback(
    async (value: string) => {
      await handleUpdatePhoneNumber(value);
    },
    [handleUpdatePhoneNumber]
  );

  const onSaveWebsite = useCallback(
    async (value: string) => {
      await handleUpdateWebsite(value);
    },
    [handleUpdateWebsite]
  );

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <PageContainer sx={{ mt: 2 }}>
      {uploadStatus && (
        <PageSection>
          <Alert
            severity={uploadStatus.error ? "error" : "success"}
            sx={{ mb: 2 }}
          >
            {uploadStatus.message}
          </Alert>
        </PageSection>
      )}

      <OrganisationSetupBanner />

      <GettingStartedChecklist
        items={checklistItems}
        onPlayVideo={(videoPath, title) =>
          setSelectedVideo({ videoPath, title })
        }
        sx={{ mb: 3 }}
      />

      <PageSection>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 3,
          }}
        >
          <OrganizationDetailsPanel
            user={user}
            canEdit={canEdit}
            isUpdatingName={isUpdatingName}
            isUpdatingDescription={isUpdatingDescription}
            isUpdatingPhoneNumber={isUpdatingPhoneNumber}
            isUpdatingWebsite={isUpdatingWebsite}
            isUpdatingHidePhoneInApp={isUpdatingHidePhoneInApp}
            onSaveName={onSaveName}
            onSaveDescription={onSaveDescription}
            onSavePhoneNumber={onSavePhoneNumber}
            onSaveWebsite={onSaveWebsite}
            onHidePhoneInAppChange={
              canEdit ? handleUpdateHidePhoneInApp : undefined
            }
          />

          <ProfileSummaryPanel
            user={user}
            canEdit={canEdit}
            logoError={logoError}
            logoVersion={logoVersion}
            logoTimestamp={logoTimestamp}
            bannerVersion={bannerVersion}
            bannerTimestamp={bannerTimestamp}
            onLogoError={handleLogoError}
            onEditLogo={() => setIsImageSelectorOpen(true)}
            onBannerFileSelect={handleBannerFileSelect}
          />
        </Box>
      </PageSection>

      {/* Email Preferences Section (team members use team JWTs, this endpoint is not supported for them) */}
      {canManageEmailPreferences && (
        <PageSection>
          <EmailPreferencesPanel token={token} />
        </PageSection>
      )}

      {/* Public Page Settings Section - Show if user can edit (even if public page not enabled yet) */}
      {canEdit && (
        <PageSection>
          <PublicPageSettingsPanel
            user={user}
            canEdit={canEdit}
            isUpdating={isUpdatingPublicPageSettings}
            isUpdatingPublicPage={isUpdatingPublicPage}
            onUpdate={handleUpdatePublicPageSettings}
            onUpdatePublicPage={handleUpdatePublicPage}
          />
        </PageSection>
      )}

      {/* Action Buttons Section */}
      <PageSection>
        <Stack id="reset-password" direction="row" spacing={2} sx={{ width: "100%" }}>
          <NoticeboardButton variant="primary" onClick={handleForgotPassword}>
            Reset Password
          </NoticeboardButton>
          <NoticeboardButton variant="danger" onClick={handleLogout}>
            Logout
          </NoticeboardButton>
        </Stack>
      </PageSection>

      <ProfileImageSelectorModal
        open={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onImageSelect={handleImageSelect}
        schoolId={user?.schoolId}
        token={token || undefined}
      />

      <ImageCropperModal
        open={isBannerCropperOpen}
        imageSrc={bannerImageToCrop || ""}
        onClose={() => {
          setIsBannerCropperOpen(false);
          setBannerImageToCrop(null);
        }}
        onCropComplete={handleBannerCropComplete}
        aspectRatio={ORGANIZATION_BANNER_ASPECT_RATIO}
      />

      <ActionModal
        open={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Upgrade Your Plan"
        subtitle="Contact Noticeboard support to upgrade your subscription"
        maxWidth="sm"
        headerStyle="light"
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            To upgrade your plan and unlock more features, please contact
            Noticeboard support or your Noticeboard customer care
            representative.
          </Typography>
          <NoticeboardButton
            variant="primary"
            onClick={() => setIsUpgradeModalOpen(false)}
            fullWidth
          >
            Close
          </NoticeboardButton>
        </Box>
      </ActionModal>

      {/* Video Player Modal */}
      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </PageContainer>
  );
};

export default Profile;
